<template>
  <div>
    <div class="tw:flex tw:items-center tw:my-6 tw:text-xl">
      <Organization class="tw:fill-gray-800 tw:mr-2 tw:size-6" />
      <p class="tw:font-bold tw:text-2xl">{{ organisation.name }}</p>
      <span class="tw:text-xs tw:text-gray-700 tw:ml-2">{{ organisation.id }}</span>
    </div>

    <BaseCard v-if="organisation.items?.length > 0" class="tw:my-6">
      <div class="tw:flex tw:items-center tw:mb-4 tw:text-xl">
        <Ownerships class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
        <p class="tw:font-bold">{{ 'Accommodations' }}</p>
      </div>

      <div class="tw:flex tw:flex-wrap tw:gap-4">
        <div
          v-for="item in organisation.items"
          :key="item.itemId"
          class="tw:border tw:border-gray-300 tw:rounded-lg tw:p-4 tw:flex-1 tw:min-w-[320px]">
          <p class="tw:font-bold tw:mb-4">{{ `Accommodation ${item.itemId}` }}</p>

          <div class="tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:justify-between">
            <div class="tw:flex tw:items-center tw:mt-2 tw:md:mt-0 tw:w-full tw:md:w-auto">
              <DangerButton
                :disabled="item.products.some(p => (p.type || '').toLowerCase() !== 'obsolete')"
                @click="openDeleteAssignmentModal(item.itemId)"
                class="tw:w-full"
                :size="'medium'"
                >{{ 'Delete assignment' }}</DangerButton
              >
              <UiTooltip
                v-if="item.products.length > 0"
                tooltip-position="top"
                :label="'You can\'t delete this assignment because the accommodation owns paid products. Please cancel them first.'">
                <InformationOutline class="tw:fill-none tw:stroke-gray-800 tw:h-6 tw:w-6 tw:ml-2" />
              </UiTooltip>
            </div>
          </div>

          <ul v-if="item.products.length > 0">
            <li v-for="(product, index) in item.products" :key="index" class="tw:border-b tw:py-4 tw:last:border-none">
              <div>
                <div>
                  <span class="tw:font-bold tw:text-gray-700">{{ 'Type: ' }}</span>
                  {{
                    product.productName === 'Rate Connect'
                      ? (rcStatusesMap[product.type] ?? product.type)
                      : product.type
                  }}
                </div>
                <div>
                  <span class="tw:font-bold tw:text-gray-700">{{ 'App Name: ' }}</span
                  >{{ product.productName }}
                </div>
                <div v-if="product.endDate">
                  <span class="tw:font-bold tw:text-gray-700">{{ 'End Date: ' }}</span
                  >{{ product.endDate }}
                </div>
              </div>

              <!-- Rate Connect: Upload Future Usages -->
              <div
                v-if="product.productName === 'Rate Connect' && isFinanceAuthority"
                class="tw:mt-3">
                <MainButton size="medium" @click="handleUploads(item.itemId)"
                  ><span v-if="!isUploading">{{ 'Upload future usages' }}</span>
                  <span v-else class="tw:min-w-[182px]"><SmallDotLoader color="#fff" /></span>
                </MainButton>
              </div>

              <!-- Business Studio Plus: Apply Voucher -->
              <div v-if="isBusinessStudioPlus(product) && canAddVoucher" class="tw:mt-3">
                <GhostButton
                  size="medium"
                  v-if="openFormItemId !== item.itemId"
                  @click="toggleStudioPlusForm(item.itemId)"
                  data-testid="admin-tool-voucher-open"
                  class="tw:flex tw:items-center tw:gap-x-1">
                  <span>{{ 'Add voucher' }}</span>
                </GhostButton>

                <transition name="slide-down">
                  <div v-show="openFormItemId === item.itemId" class="tw:mt-4 tw:bg-gray-100 tw:p-4 tw:rounded-lg">
                    <div class="tw:flex tw:flex-col tw:gap-3">
                      <BaseTextField
                        label="Accommodation Id"
                        name="accommodationId"
                        aria-label="accommodationId"
                        data-testid="admin-tool-voucher-accommodation-id"
                        size="medium"
                        type="number"
                        class="tw:w-full"
                        :value="item.itemId"
                        :disabled="true" />

                      <BaseTextField
                        label="Voucher"
                        name="voucherCode"
                        aria-label="voucherCode"
                        data-testid="admin-tool-voucher-code"
                        size="medium"
                        type="text"
                        class="tw:w-full"
                        :value="voucherValue"
                        @input="e => setVoucherValue(e)" />
                      <div class="tw:flex tw:gap-2 tw:justify-end">
                        <GhostButton
                          size="medium"
                          data-testid="admin-tool-voucher-cancel"
                          @click="toggleStudioPlusForm(item.itemId)">
                          {{ 'Cancel' }}
                        </GhostButton>
                        <MainButton
                          size="medium"
                          data-testid="admin-tool-voucher-apply"
                          @click="handleStudioPlusSubmit(item.itemId)"
                          :disabled="voucherValue.trim().length === 0 || isSubmittingStudioPlus">
                          <span v-if="!isSubmittingStudioPlus">{{ 'Apply voucher' }}</span>
                          <span v-else><SmallDotLoader color="#fff" /></span>
                        </MainButton>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- Rate Connect: Apply Voucher -->
              <div v-if="isActiveRateConnect(product) && canAddVoucher" class="tw:mt-3">
                <GhostButton
                  size="medium"
                  v-if="openRCFormItemId !== item.itemId"
                  @click="toggleRCForm(item.itemId)"
                  data-testid="admin-tool-rc-voucher-open"
                  class="tw:flex tw:items-center tw:gap-x-1">
                  <span>{{ 'Add RC voucher' }}</span>
                </GhostButton>

                <transition name="slide-down">
                  <div v-show="openRCFormItemId === item.itemId" class="tw:mt-4 tw:bg-gray-100 tw:p-4 tw:rounded-lg">
                    <div class="tw:flex tw:flex-col tw:gap-3">
                      <BaseTextField
                        :id="`rc-voucher-accommodation-id-${item.itemId}`"
                        label="Accommodation Id"
                        name="rcAccommodationId"
                        aria-label="rcAccommodationId"
                        data-testid="admin-tool-rc-voucher-accommodation-id"
                        size="medium"
                        type="number"
                        class="tw:w-full"
                        :value="item.itemId"
                        :disabled="true" />
                      <BaseTextField
                        :id="`rc-voucher-code-${item.itemId}`"
                        label="Voucher"
                        name="rcVoucherCode"
                        aria-label="rcVoucherCode"
                        data-testid="admin-tool-rc-voucher-code"
                        size="medium"
                        type="text"
                        class="tw:w-full"
                        :value="rcVoucherValue"
                        @input="e => setRCVoucherValue(e)" />
                      <div class="tw:flex tw:gap-2 tw:justify-end">
                        <GhostButton
                          size="medium"
                          data-testid="admin-tool-rc-voucher-cancel"
                          @click="toggleRCForm(item.itemId)">
                          {{ 'Cancel' }}
                        </GhostButton>
                        <MainButton
                          size="medium"
                          data-testid="admin-tool-rc-voucher-apply"
                          @click="handleRCSubmit(item.itemId)"
                          :disabled="rcVoucherValue.trim().length === 0 || isSubmittingRC">
                          <span v-if="!isSubmittingRC">{{ 'Apply voucher' }}</span>
                          <span v-else><SmallDotLoader color="#fff" /></span>
                        </MainButton>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </BaseCard>

    <BaseCard v-if="organisation.users?.length > 0 || organisation.invitations?.length > 0" class="tw:my-6">
      <div class="tw:flex tw:items-center tw:mb-4 tw:text-xl">
        <Users class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
        <p class="tw:font-bold">{{ 'Users' }}</p>
      </div>
      <OrgUsers
        :users="organisation.users"
        :invitations="organisation.invitations"
        :organisationId="organisation.id"
        @fetchData="fetchData" />
    </BaseCard>

    <BaseCard v-if="canInvite || canSelfJoin" class="tw:my-6">
      <div class="tw:flex tw:items-center tw:mb-4 tw:text-xl">
        <Users class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
        <p class="tw:font-bold">{{ canInvite ? 'Invite user' : 'Join organisation' }}</p>
      </div>
      <div>
        <InviteUser
          v-if="canInvite"
          :organisationId="organisation.id"
          @fetchData="fetchData"
          :users="organisation.users" />
        <JoinOrganisation v-else :organisationId="organisation.id" :users="organisation.users" @fetchData="fetchData" />
      </div>
    </BaseCard>

    <DeleteAssignmentModal
      :isOpen="isDeleteAssignmentModalOpen"
      :isDeleting="isDeletingAssignment"
      @toggleModal="toggleDeleteAssignmentModal"
      @deleteAssignment="confirmDeleteAssignment" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import Organization from '@/components/icons/Organization.svg?component'
import Ownerships from '@/components/icons/Ownerships.svg?component'
import Users from '@/components/icons/Users.svg?component'
import DangerButton from '@/components/base/buttons/DangerButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import OrgUsers from '@/components/adminTool/OrgUsers.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'
import InviteUser from '@/components/adminTool/InviteUser.vue'
import JoinOrganisation from '@/components/adminTool/JoinOrganisation.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import DeleteAssignmentModal from '@/components/adminTool/DeleteAssignmentModal.vue'
import BaseTextField from '@/components/base/inputs/BaseTextField.vue'
import useToastNotifications from '@/components/notifications/useToastNotifications'

import { useApplyVoucher, useApplyRCVoucher, useDeleteAssignment, useFutureUsages } from './queries'
import { authorities } from '@/constants/authorities'
import { useSessionValues } from '@/composables/useSessionValues'

defineProps({
  organisation: Object,
})

const emit = defineEmits(['fetchData'])
const fetchData = () => emit('fetchData')

const { displayNotification } = useToastNotifications()
const { userAuthorities } = useSessionValues()
const { applyVoucher } = useApplyVoucher()
const { applyRCVoucher } = useApplyRCVoucher()
const { uploadFutureUsages } = useFutureUsages()
const { isPending: isDeletingAssignment, deleteAssignment } = useDeleteAssignment()

const canInvite = computed(() => userAuthorities.value.includes(authorities['ADMIN_TOOL_SPECIAL_AUTHORITY']))
const canAddVoucher = computed(() => userAuthorities.value.includes(authorities['ADMIN_TOOL_AUTHORITY']))
const isFinanceAuthority = computed(() => userAuthorities.value.includes(authorities['FINANCE_AUTHORITY']))
const { hasAdminToolLimitedAuthority } = useSessionValues()
const canSelfJoin = computed(() => !canInvite.value && hasAdminToolLimitedAuthority.value)

// Delete assignment modal state (per item, one modal instance reused)
const isDeleteAssignmentModalOpen = ref(false)
const selectedItemId = ref(null)

const openDeleteAssignmentModal = itemId => {
  selectedItemId.value = itemId
  isDeleteAssignmentModalOpen.value = true
}

const toggleDeleteAssignmentModal = value => {
  isDeleteAssignmentModalOpen.value = value
}

const confirmDeleteAssignment = () => {
  if (!selectedItemId.value) return

  deleteAssignment(
    { accommodationId: selectedItemId.value },
    {
      onSuccess: () => {
        isDeleteAssignmentModalOpen.value = false
        displayNotification({
          title: 'Success!',
          message: 'The accommodation was unassigned from the Studio Organization',
          type: 'success',
        })
        fetchData()
      },
      onError: error => {
        isDeleteAssignmentModalOpen.value = false
        const backendMessage = error.response?.data?.message
        displayNotification({
          title: 'Error!',
          message: backendMessage ?? 'something_went_wrong',
          isTranslationKey: !backendMessage,
          type: 'error',
        })
      },
    }
  )
}

// Rate Connect: Upload Future Usages state
const isUploading = ref(false)

const handleUploads = accommodationId => {
  isUploading.value = true
  uploadFutureUsages(
    { accommodationId },
    {
      onSuccess: () => {
        isUploading.value = false
        displayNotification({
          message: 'Action performed successfully',
          isTranslationKey: false,
          isHtml: false,
          type: 'success',
        })
      },
      onError: error => {
        isUploading.value = false
        const backendMessage = error.response?.data?.message
        const message = backendMessage ?? error.message ?? 'something_went_wrong'
        displayNotification({
          message,
          isTranslationKey: !backendMessage && !error.message,
          isHtml: false,
          type: 'error',
        })
      },
    }
  )
}

// Studio Plus voucher form state
const openFormItemId = ref(null)
const isSubmittingStudioPlus = ref(false)
const voucherValue = ref('')
const setVoucherValue = e => {
  voucherValue.value = e.target.value
}
const toggleStudioPlusForm = itemId => {
  openFormItemId.value = openFormItemId.value === itemId ? null : itemId
  voucherValue.value = ''
}

const isBusinessStudioPlus = product => {
  const productType = product.type?.toLowerCase() || ''
  return (
    product.productName === 'Business Studio Plus' &&
    product?.endDate === null &&
    (productType.includes('yearly') || productType.includes('annual'))
  )
}

const handleStudioPlusSubmit = itemId => {
  if (isSubmittingStudioPlus.value || voucherValue.value.trim().length === 0) return
  isSubmittingStudioPlus.value = true

  applyVoucher(
    {
      accommodationId: itemId,
      voucherCode: voucherValue.value.trim(),
    },
    {
      onSuccess: () => {
        isSubmittingStudioPlus.value = false
        voucherValue.value = ''
        openFormItemId.value = null
        displayNotification({
          message: 'Action performed successfully',
          isTranslationKey: false,
          isHtml: false,
          type: 'success',
        })
        fetchData()
      },
      onError: error => {
        isSubmittingStudioPlus.value = false
        voucherValue.value = ''
        const backendMessage = error.response?.data?.message
        displayNotification({
          message: backendMessage ?? 'something_went_wrong',
          isTranslationKey: !backendMessage,
          isHtml: false,
          type: 'error',
        })
      },
    }
  )
}

// Rate Connect voucher form state
const openRCFormItemId = ref(null)
const isSubmittingRC = ref(false)
const rcVoucherValue = ref('')
const setRCVoucherValue = e => {
  rcVoucherValue.value = e.target.value
}
const toggleRCForm = itemId => {
  openRCFormItemId.value = openRCFormItemId.value === itemId ? null : itemId
  rcVoucherValue.value = ''
}

const isActiveRateConnect = product => product.productName === 'Rate Connect' && !product.endDate

const handleRCSubmit = itemId => {
  if (isSubmittingRC.value || rcVoucherValue.value.trim().length === 0) return
  isSubmittingRC.value = true

  applyRCVoucher(
    {
      accommodationId: itemId,
      voucherCode: rcVoucherValue.value.trim(),
    },
    {
      onSuccess: () => {
        isSubmittingRC.value = false
        rcVoucherValue.value = ''
        openRCFormItemId.value = null
        displayNotification({
          message: 'Action performed successfully',
          isTranslationKey: false,
          isHtml: false,
          type: 'success',
        })
        fetchData()
      },
      onError: error => {
        isSubmittingRC.value = false
        rcVoucherValue.value = ''
        const backendMessage = error.response?.data?.message
        displayNotification({
          message: backendMessage ?? 'something_went_wrong',
          isTranslationKey: !backendMessage,
          isHtml: false,
          type: 'error',
        })
      },
    }
  )
}

const rcStatusesMap = {
  OBSOLETE: 'OBSOLETE',
  PAUSED: 'PAUSED',
  PAUSED_FOR_BUDGET: 'PAUSED BECAUSE OF BUDGET',
  PAUSED_UNTIL: 'PAUSED UNTIL',
  ACTIVE: 'ACTIVE',
}
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
